import React, { useState, useEffect, useRef } from "react";
import styles from "./PositionChangeNotification.module.css";

// The notification shape produced by usePositionNotifications. id/timestamp are
// populated by the hook but unused by the component, so they're optional here so
// callers/tests can render with just the displayed fields (type/title/message).
export interface PositionNotification {
  id?: number;
  type: string;
  title: string;
  message?: string | null;
  timestamp?: Date;
}

interface PositionChangeNotificationProps {
  notification: PositionNotification | null;
  onClose: () => void;
  duration?: number;
}

const PositionChangeNotification = ({
  notification,
  onClose,
  duration = 4000,
}: PositionChangeNotificationProps): React.ReactElement | null => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      setIsLeaving(false);

      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => {
        clearTimeout(timer);
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, duration]);

  const handleClose = () => {
    setIsLeaving(true);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      onClose();
      closeTimeoutRef.current = null;
    }, 200); // Match animation duration
  };

  if (!notification || !isVisible) return null;

  const getNotificationIcon = (type: string): string => {
    switch (type) {
      case "denied":
        return "⚠️";
      case "waiting":
        return "⏳";
      default:
        return "ℹ️";
    }
  };

  const getNotificationClass = (type: string): string => {
    switch (type) {
      case "denied":
        return "warning";
      case "waiting":
        return "info";
      default:
        return "info";
    }
  };

  return (
    <div
      className={`${styles["position-notification"]} ${styles[getNotificationClass(notification.type)]} ${isLeaving ? styles["leaving"] : ""}`}
      onClick={handleClose}
    >
      <div className={styles["notification-content"]}>
        <span className={styles["notification-icon"]}>
          {getNotificationIcon(notification.type)}
        </span>
        <div className={styles["notification-text"]}>
          <div className={styles["notification-title"]}>
            {notification.title}
          </div>
          {notification.message && (
            <div className={styles["notification-message"]}>
              {notification.message}
            </div>
          )}
        </div>
        <button
          className={styles["notification-close"]}
          onClick={handleClose}
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Hook for managing position change notifications
export const usePositionNotifications = () => {
  const [notification, setNotification] = useState<PositionNotification | null>(
    null,
  );

  const showNotification = (
    type: string,
    title: string,
    message: string | null = null,
  ) => {
    setNotification({
      id: Date.now(),
      type,
      title,
      message,
      timestamp: new Date(),
    });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  // Helper methods for common notifications
  const notifyDenied = (reason?: string | null) => {
    showNotification(
      "denied",
      "Pass request denied",
      reason || "Your pass request was denied.",
    );
  };

  const notifyWaiting = (destinationName?: string | null) => {
    const dest = destinationName || "Your destination";
    showNotification(
      "waiting",
      "Destination full",
      `${dest} is full — you'll be let in as soon as a spot opens up.`,
    );
  };

  return {
    notification,
    showNotification,
    clearNotification,
    notifyDenied,
    notifyWaiting,
  };
};

export default PositionChangeNotification;
